import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Calendar, Clock, Tag, User as UserIcon,
  PlayCircle, ExternalLink, FileText, MessageCircle,
  Linkedin, Mail, Plus, Users, Briefcase, Target, Handshake, Search
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function resolveImg(url) {
  if (!url) return "";
  return url.startsWith("/api/uploads/") ? `${API}${url}` : url;
}

function formatDateLong(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

function getEmbedUrl(url) {
  if (!url) return null;
  // YouTube
  let m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  // Vimeo
  m = url.match(/vimeo\.com\/(\d+)/);
  if (m) return `https://player.vimeo.com/video/${m[1]}`;
  // Loom
  m = url.match(/loom\.com\/share\/([\w-]+)/);
  if (m) return `https://www.loom.com/embed/${m[1]}`;
  return null;
}

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/api/events/${id}`, { withCredentials: true })
      .then(({ data }) => setEvent(data))
      .catch(err => setError(err.response?.data?.detail || "Événement introuvable"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="cbm-skeleton h-8 w-32 mb-5 rounded-lg" />
      <div className="cbm-skeleton rounded-xl" style={{ height: 300 }} />
    </div>
  );

  if (error || !event) return (
    <div className="p-6 text-center">
      <p className="text-cbm-muted">{error || "Événement introuvable"}</p>
      <button className="cbm-btn-ghost mt-3" onClick={() => navigate("/evenements")} data-testid="back-to-events">
        Retour aux événements
      </button>
    </div>
  );

  const embedUrl = getEmbedUrl(event.replay_url);
  const speaker = event.speaker_profile;
  // Prefer explicit status; fallback to date for legacy events without status
  const isPast = event.status
    ? event.status === "past"
    : (event.event_date && new Date(event.event_date) < new Date());
  const hasCtas = isPast && (event.show_contact_speaker || event.show_post_opportunity || event.show_view_directory);

  const handleContactSpeaker = () => {
    if (!speaker) return;
    if (speaker.whatsapp) {
      window.open(`https://wa.me/${speaker.whatsapp.replace(/\D/g, "")}`, "_blank");
    } else if (speaker.email) {
      window.open(`mailto:${speaker.email}`);
    } else if (speaker.linkedin_url) {
      window.open(speaker.linkedin_url, "_blank");
    } else {
      navigate(`/profil/${speaker.id}`);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto animate-fade-in" data-testid="event-detail-page">
      {/* Back */}
      <button
        className="cbm-btn-ghost text-sm mb-5"
        onClick={() => navigate("/evenements")}
        data-testid="back-button"
      >
        <ArrowLeft size={16} /> Retour aux événements
      </button>

      {/* Header — image + infos */}
      <div className="cbm-card overflow-hidden mb-5 animate-fade-in-up">
        {event.image_url && (
          <div className="aspect-video overflow-hidden" style={{ background: "#071028" }}>
            <img
              src={resolveImg(event.image_url)}
              alt={event.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <div className="p-6">
          <div className="flex items-center gap-1.5 flex-wrap mb-3">
            {event.event_type && <span className="cbm-badge cbm-badge-verified">{event.event_type}</span>}
            {event.theme_tag && (
              <span className="cbm-badge cbm-badge-open"><Tag size={9} className="mr-0.5" />{event.theme_tag}</span>
            )}
            {isPast && event.replay_url && <span className="cbm-badge cbm-badge-featured">Replay disponible</span>}
          </div>
          <h1 className="font-outfit text-white mb-3" style={{ fontSize: "1.75rem", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.15 }}>
            {event.title}
          </h1>
          <div className="flex items-center gap-5 flex-wrap" style={{ color: "#CFD9EC", fontSize: "0.9375rem" }}>
            <span className="flex items-center gap-1.5">
              <Calendar size={14} className="text-gold" />
              <span className="capitalize">{formatDateLong(event.event_date)}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <Clock size={14} className="text-gold" />
              {formatTime(event.event_date)}
            </span>
            {event.speaker && (
              <span className="flex items-center gap-1.5">
                <UserIcon size={14} className="text-gold" />
                {event.speaker}
              </span>
            )}
          </div>
          <p className="mt-4 leading-relaxed" style={{ color: "#CFD9EC", fontSize: "0.9375rem", lineHeight: 1.6 }}>
            {event.description}
          </p>
        </div>
      </div>

      {/* Upcoming CTA — Participer */}
      {!isPast && (
        <section className="mb-5 animate-fade-in-up-delay-1" data-testid="upcoming-cta">
          <div className="cbm-card p-5 flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="font-outfit font-semibold text-white text-base">Inscription</p>
              <p className="text-cbm-muted text-xs mt-0.5">
                {event.registration_url ? "Réservez votre place via Tally" : "Le lien d'inscription sera disponible prochainement"}
              </p>
            </div>
            {event.registration_url ? (
              <a
                href={event.registration_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="register-cta"
                className="cbm-btn-primary text-sm"
              >
                <ExternalLink size={14} /> Participer
              </a>
            ) : (
              <span className="cbm-btn-ghost text-sm border border-white/10 cursor-default opacity-60">
                Inscription à venir
              </span>
            )}
          </div>
        </section>
      )}

      {/* Replay — only for past events */}
      {isPast && event.replay_url && (
        <section className="mb-5 animate-fade-in-up-delay-1" data-testid="replay-section">
          <h2 className="font-outfit font-semibold text-white text-base mb-3 flex items-center gap-2">
            <PlayCircle size={16} className="text-gold" /> Replay
          </h2>
          <div className="cbm-card overflow-hidden">
            {embedUrl ? (
              <div className="aspect-video bg-black">
                <iframe
                  src={embedUrl}
                  title={event.title}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid="replay-iframe"
                />
              </div>
            ) : (
              <div className="p-8 text-center">
                <PlayCircle size={36} className="text-gold mx-auto mb-3" />
                <p className="text-cbm-secondary text-sm mb-4">
                  Le replay est disponible sur une plateforme externe.
                </p>
                <a
                  href={event.replay_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cbm-btn-primary text-sm"
                  data-testid="replay-external-button"
                >
                  <PlayCircle size={14} /> Ouvrir le replay <ExternalLink size={11} />
                </a>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Action block — only for past events */}
      {isPast && (
        <section className="mb-5 animate-fade-in-up-delay-1" data-testid="action-block">
          <div
            className="rounded-xl p-5 sm:p-6"
            style={{
              background: "linear-gradient(135deg, rgba(201,162,39,0.10), rgba(201,162,39,0.02))",
              border: "1px solid rgba(201,162,39,0.28)",
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
            }}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "rgba(201,162,39,0.18)", border: "1px solid rgba(201,162,39,0.35)" }}>
                <Target size={18} className="text-gold" />
              </div>
              <div>
                <h2 className="font-outfit font-semibold text-white text-base leading-tight">
                  Passe à l'action après ce workshop
                </h2>
                <p className="text-sm mt-1" style={{ color: "#CFD9EC" }}>
                  Transformez ce replay en opportunités concrètes avec les membres du club.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button
                data-testid="cta-post-need"
                onClick={() => {
                  const params = new URLSearchParams({
                    type: "cherche",
                    titre: `Suite au workshop : ${event.title}`,
                    description: event.theme_tag ? `Thème : ${event.theme_tag}` : "",
                  });
                  navigate(`/poster-annonce?${params.toString()}`);
                }}
                className="cbm-card p-4 text-left hover:border-gold/40 transition-all"
              >
                <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2" style={{ background: "rgba(201,162,39,0.12)" }}>
                  <Plus size={16} className="text-gold" />
                </div>
                <p className="text-white font-semibold text-sm">Publier un besoin</p>
                <p className="text-cbm-muted text-xs mt-0.5">Lié directement à ce workshop</p>
              </button>

              <button
                data-testid="cta-propose-collab"
                onClick={() => {
                  const params = new URLSearchParams({
                    type: "propose",
                    titre: `Collaboration autour de : ${event.title}`,
                    description: event.theme_tag ? `Thème : ${event.theme_tag}` : "",
                  });
                  navigate(`/poster-annonce?${params.toString()}`);
                }}
                className="cbm-card p-4 text-left hover:border-gold/40 transition-all"
              >
                <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2" style={{ background: "rgba(201,162,39,0.12)" }}>
                  <Handshake size={16} className="text-gold" />
                </div>
                <p className="text-white font-semibold text-sm">Proposer une collaboration</p>
                <p className="text-cbm-muted text-xs mt-0.5">Partagez votre offre avec le club</p>
              </button>

              <button
                data-testid="cta-find-members"
                onClick={() => {
                  if (event.theme_tag) {
                    navigate(`/annuaire?search=${encodeURIComponent(event.theme_tag)}`);
                  } else {
                    navigate("/annuaire");
                  }
                }}
                className="cbm-card p-4 text-left hover:border-gold/40 transition-all"
              >
                <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2" style={{ background: "rgba(201,162,39,0.12)" }}>
                  <Search size={16} className="text-gold" />
                </div>
                <p className="text-white font-semibold text-sm">Trouver des membres</p>
                <p className="text-cbm-muted text-xs mt-0.5">
                  {event.theme_tag ? `Sur le thème « ${event.theme_tag} »` : "Sur ce sujet"}
                </p>
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Resources — only for past events */}
      {isPast && event.resources && event.resources.length > 0 && (
        <section className="mb-5 animate-fade-in-up-delay-2" data-testid="resources-section">
          <h2 className="font-outfit font-semibold text-white text-base mb-3 flex items-center gap-2">
            <FileText size={16} className="text-gold" /> Ressources
          </h2>
          <div className="cbm-card p-4 space-y-2">
            {event.resources.map((r, i) => (
              <a
                key={i}
                href={r.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`resource-${i}`}
                className="flex items-center justify-between gap-3 p-3 rounded-lg border border-white/10 hover:border-gold/40 hover:bg-gold/5 transition-all group"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: "rgba(201,162,39,0.12)", border: "1px solid rgba(201,162,39,0.25)" }}>
                    <FileText size={14} className="text-gold" />
                  </div>
                  <span className="text-white text-sm font-medium truncate">{r.title}</span>
                </div>
                <ExternalLink size={14} className="text-cbm-muted group-hover:text-gold transition-colors flex-shrink-0" />
              </a>
            ))}
          </div>
        </section>
      )}

      {/* Speaker */}
      {speaker && (
        <section className="mb-5 animate-fade-in-up-delay-2" data-testid="speaker-section">
          <h2 className="font-outfit font-semibold text-white text-base mb-3 flex items-center gap-2">
            <UserIcon size={16} className="text-gold" /> Intervenant
          </h2>
          <div className="cbm-card p-5 flex items-center gap-4">
            {speaker.photo ? (
              <img
                src={speaker.photo.startsWith("/api/") ? `${API}${speaker.photo}` : speaker.photo}
                alt={speaker.nom_prenom}
                className="w-14 h-14 rounded-full object-cover border border-gold/30"
              />
            ) : (
              <div className="cbm-avatar w-14 h-14 text-base flex-shrink-0">
                {(speaker.nom_prenom || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-outfit font-semibold text-white" style={{ fontSize: "1.0625rem" }}>{speaker.nom_prenom}</p>
              {speaker.entreprise && <p className="text-sm mt-0.5" style={{ color: "#E6EDF7", fontWeight: 500 }}>{speaker.entreprise}</p>}
            </div>
            <button
              data-testid="view-speaker-profile"
              onClick={() => navigate(`/profil/${speaker.id}`)}
              className="cbm-btn-secondary text-sm"
            >
              <ExternalLink size={13} /> Voir profil
            </button>
          </div>
        </section>
      )}

      {/* CTAs */}
      {hasCtas && (
        <section className="animate-fade-in-up-delay-3" data-testid="event-ctas">
          <h2 className="font-outfit font-semibold text-white text-base mb-3">Aller plus loin</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {event.show_contact_speaker && speaker && (
              <button
                data-testid="cta-contact-speaker"
                onClick={handleContactSpeaker}
                className="cbm-card p-4 text-left hover:border-gold/40 transition-all"
              >
                <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2" style={{ background: "rgba(201,162,39,0.12)" }}>
                  <MessageCircle size={16} className="text-gold" />
                </div>
                <p className="text-white font-semibold text-sm">Contacter l'intervenant</p>
                <p className="text-cbm-muted text-xs mt-0.5">Échanger directement avec {speaker.nom_prenom.split(" ")[0]}</p>
              </button>
            )}
            {event.show_post_opportunity && (
              <button
                data-testid="cta-post-opportunity"
                onClick={() => navigate("/poster-annonce")}
                className="cbm-card p-4 text-left hover:border-gold/40 transition-all"
              >
                <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2" style={{ background: "rgba(201,162,39,0.12)" }}>
                  <Plus size={16} className="text-gold" />
                </div>
                <p className="text-white font-semibold text-sm">Poster une annonce</p>
                <p className="text-cbm-muted text-xs mt-0.5">Partagez un besoin ou une offre au club</p>
              </button>
            )}
            {event.show_view_directory && (
              <button
                data-testid="cta-view-directory"
                onClick={() => navigate("/annuaire")}
                className="cbm-card p-4 text-left hover:border-gold/40 transition-all"
              >
                <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2" style={{ background: "rgba(201,162,39,0.12)" }}>
                  <Users size={16} className="text-gold" />
                </div>
                <p className="text-white font-semibold text-sm">Voir l'annuaire</p>
                <p className="text-cbm-muted text-xs mt-0.5">Découvrez les membres du club</p>
              </button>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
